import React, { useState, useRef } from "react";
import { SketchPicker } from "react-color";

import { AppModal } from "src/components/UI/common/AppModal";
import { ModalShell } from "src/components/UI/common/ModalShell";
import { Box, Button, Field, Input, Stack, MetreaTooltip } from "@metrea-ui/v2";
import { useClickAway } from "src/hooks/useClickAway";

import { SortableList } from "src/components/UI/SortableList/SortableList";
import ModalBox from "src/components/UI/Modal/NestedModal";
import FormTextField from "src/components/UI/common/FormTextField";

import { mColor } from "src/utils/themeUtils";
import { Session } from "src/utils/sessionUtils";

const SortingModal = (props) => {
	const [open, setOpen] = useState(true);

	const handleClose = () => {
		setOpen(false);
		props.onClose();
	};

	return (
		<AppModal
			open={open}
			onClose={handleClose}
			aria-labelledby="child-modal-title"
			aria-describedby="child-modal-description"
		>
			<Box>
				<ModalShell
					top="50%"
					left="50%"
					transform="translate(-50%, -50%)"
					contentOverflowY="hidden"
					width={props.width || "500px"}
					maxWidth={props.width || 500}
					maxHeight={props.height || "75vh"}
					overflow="hidden" // prevent modal scroll
				>
					<Box height="100%" overflow="hidden">
						<p id="sorting-modal-description">{/* {props.description} */}</p>
						{props.children}
					</Box>
				</ModalShell>
			</Box>
		</AppModal>
	);
};

export const ManageChip = (props) => {
	const pickerRef = useRef(null);

	const renderReadonlyField = (label, id, value, tooltipContent, onClick) => (
		<Field.Root style={{ width: "100%", marginBottom: "0.5rem" }}>
			<Field.Label htmlFor={id}>{label}</Field.Label>
			<MetreaTooltip content={tooltipContent}>
				{/* Anchor the tooltip to a plain wrapper element because the direct v2
				    Input is not a stable tooltip trigger in this modal path. */}
				<Box
					as="button"
					type="button"
					aria-label={`${label}. ${tooltipContent}`}
					onClick={onClick}
					style={{
						width: "100%",
						cursor: "pointer",
						background: "none",
						border: "none",
						padding: 0,
						textAlign: "left",
					}}
				>
					<Input
						id={id}
						name={id}
						size="sm"
						variant="subtle"
						value={value}
						readOnly
						pointerEvents="none"
						width="100%"
						style={{
							width: "100%",
							fontSize: "0.8rem",
							cursor: "pointer",
						}}
					/>
				</Box>
			</MetreaTooltip>
		</Field.Root>
	);
	const renderColorField = (label, id, value, tooltipContent, onClick) => (
		<Field.Root style={{ width: "100%", marginBottom: "0.5rem" }}>
			<Field.Label htmlFor={id}>{label}</Field.Label>
			<MetreaTooltip content={tooltipContent}>
				<Box
					id={id}
					as="button"
					type="button"
					aria-label={`${label}. ${tooltipContent}`}
					onClick={onClick}
					style={{
						width: "100%",
						cursor: "pointer",
						backgroundColor: "var(--chakra-colors-color-palette-subtle, #e8edf2)",
						border: "none",
						padding: "8px 12px",
						display: "flex",
						alignItems: "center",
						gap: "12px",
						minHeight: "34px",
						textAlign: "left",
					}}
				>
					<Box
						aria-hidden="true"
						style={{
							width: "16px",
							height: "16px",
							backgroundColor: value,
							border: "1px solid rgba(0,0,0,0.18)",
							flexShrink: 0,
						}}
					/>
					<span
						style={{
							fontSize: "0.8rem",
							color: "var(--chakra-colors-fg-default, inherit)",
						}}
					>
						{value}
					</span>
				</Box>
			</MetreaTooltip>
		</Field.Root>
	);
	const renderTextField = (props) => (
		<FormTextField {...props} size="sm" helperColor={mColor.Danger} marginTop={0} marginBottom={0} />
	);

	const [chip, setChip] = useState(props.item);
	const [, setModalOpen] = useState(false);
	const [sortingList, setSortingList] = useState(props.list);
	const [sortingModal, setSortingModal] = useState(false);
	const [valueError, setValueError] = useState("");
	const [bgColor, setBgColor] = useState(chip.color ? chip.color : "#FFFFFF");
	const [colorPicker, setColorPicker] = useState(false);

	const modalOpenHandler = (open) => {
		setModalOpen(open);
		props.onSetOpen(open);
	};

	const getSortingIdx = () => {
		// console.log(sortingList.find(chip.id));
		// console.log("getSortingIdx");
		// console.log(sortingList);
		// console.log(id);
		return sortingList.findIndex((x) => x.id === chip.id) + 1;
	};

	const sortingClickHandler = () => {
		if (colorPicker) onExitColorPicker();
		else setSortingModal(!sortingModal);
	};

	const sortingChangeHandler = (list) => {
		// debugLog(10, "[ManageChip] sortingChangeHandler", { list: list });

		setSortingList(list);
		setSortingModal(false);
		props.onSave(list);
	};

	const handleInputChange = (event) => {
		const { name, value } = event.target;

		if (name === "comp_value") {
			setValueError(value && value.trim() ? "" : "New Value is required!");
		}

		setChip({
			...chip,
			[name]: value,
			update_flag: 102,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		});

		let arrayNew = [];
		sortingList.map((x, index) => {
			if (x.id === chip.id) {
				arrayNew.push(chip);
			} else {
				arrayNew.push(x);
			}
			return false;
		});

		setSortingList(arrayNew);

		props.onClearComplete();
	};

	const changeColorHandler = (color) => {
		// debugLog(10, "[ManageChip] changeColorHandler", { color: color });

		setChip({
			...chip,
			color: color.hex,
			update_flag: 102,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		});
		setBgColor(color.hex);

		let arrayNew = [];
		sortingList.map((x, index) => {
			if (x.id === chip.id) {
				arrayNew.push(chip);
			} else {
				arrayNew.push(x);
			}
			return false;
		});
		setSortingList(arrayNew);
	};

	const onExitColorPicker = () => {
		let arrayNew = [];
		sortingList.map((x, index) => {
			if (x.id === chip.id) {
				arrayNew.push(chip);
			} else {
				arrayNew.push(x);
			}
			return false;
		});
		setSortingList(arrayNew);
		props.onClearComplete();

		setColorPicker(false);
	};

	useClickAway(pickerRef, onExitColorPicker);

	const colorClickHandler = (event) => {
		setColorPicker(true);
	};

	const saveHandler = (event) => {
		// debugLog(10, "[ManageChip] saveHandler", { props: props, chip: chip });

		let newArray = [];
		props.list.map((x) => {
			if (x.id === chip.id) {
				newArray.push(chip);
			} else {
				newArray.push(x);
			}
			return false;
		});

		props.onSave(newArray);

		setModalOpen(false);
		props.onSetOpen(false);
	};

	const valueProps = valueError ? { error: true, helperText: valueError } : {};

	// debugLog(10, "[ManageChip]", {
	// 	props: props,
	// 	chip: chip,
	// 	fontColor: fontColor,
	// 	sortingList: sortingList,
	// });

	return (
		<>
			<ModalBox
				title={props.title}
				onSetOpen={modalOpenHandler}
				inset="unset"
				width="500px"
				minHeight={360}
				maxHeight="70vh"
				child={false}
			>
				<Box>
					{/* Preserve the compact modal layout here: rely on explicit field margins, not the Stack gap. */}
					<Stack gap={2}>
						<Box marginBottom="24px">
							<h5>Item: {props.item.comp_value}</h5>
						</Box>
						{renderTextField({
							id: "comp_value",
							name: "comp_value",
							label: "New Value",
							value: chip?.comp_value ?? "",
							onChange: handleInputChange,
							required: true,
							errorText: valueProps.helperText || "",
						})}

						{colorPicker && (
							<Box
								ref={pickerRef}
								marginTop="20%"
								marginLeft="40%"
								position="absolute"
								zIndex="overlay"
							>
								<SketchPicker
									disableAlpha
									width="300px"
									color={bgColor}
									onChangeComplete={changeColorHandler}
								/>
							</Box>
						)}

						{renderColorField(
							"Color",
							"color",
							bgColor !== "null" ? bgColor.toUpperCase() : mColor.White,
							"Click to change color",
							colorClickHandler,
						)}

						{renderReadonlyField(
							"Position in the list",
							"sort_id",
							getSortingIdx() + " / " + sortingList.length,
							"Click to change sorting position",
							sortingClickHandler,
						)}

						<Box marginTop={20}>
							<Button variant="solid" onClick={saveHandler}>
								Save
							</Button>
						</Box>
					</Stack>
				</Box>
			</ModalBox>

			{sortingModal && (
				<SortingModal onClose={sortingClickHandler} width="500px" height="75vh">
					<SortableList
						items={sortingList}
						item={chip}
						onChange={sortingChangeHandler}
						width="100%"
					/>
				</SortingModal>
			)}
		</>
	);
};

export default ManageChip;
